(ns app.front.view.group.admin.user-management
  (:require ["@carbon/pictograms-react/es/single--sign-on" :default SingleSignOn]
            [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.external-module.model :as external-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.yes-no-radio-buttons :as yes-no]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.validate :as validate]
            [app.front.view.group.admin.add-user :as add-user]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.mfa.core :as group.admin.mfa]
            [app.front.view.group.admin.remove-user :as remove-user]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.users.service-accounts :as service-accounts]
            [app.front.view.user-management.model :as user-management-model]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

;; TODO remove when Carbon is updated and cc/radio-button state works correctly.
;; Force viewer only selection redraw so that cancelling the selection is possible.
(def *viewer-only-force (r/atom 0))

(def user-details-model {:user/first-name [[validate/NonBlankString :user-management/error-first-name]]
                         :user/last-name  [[validate/NonBlankString :user-management/error-last-name]]
                         :user/groups     [[validate/NotEmptyList :user-management/error-groups]]})

(def debug (rf/after (fn [_ event]
                       (.log js/console "=======")
                       (.log js/console "event: " (str event)))))

(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])

;; --------------------------------------------
;; Edit state functionality
;;
;; The idea is that each user is "mirrored" by an edit state instance that can be edited by the
;; user. Successfully validated changes are eventually sent to the backend and later propagated to the
;; underlying user data.

(defn group-with-checked [user {gid   :db/id
                                admin :admin-group?
                                :as   group}]
  [gid (merge (select-keys group [:db/id :group/name :group/roles :group/external-kind
                                  :used-in-group-config? :qualification-names])
              {:checked?     (boolean (p/find-first (:user/groups user)
                                                    {:db/id gid}))
               :admin-group? admin})])

(defn create-edit-state
  "Edit state contents are otherwise similar to `user` with the excption
  of :groups, which is group-id - contents map, where contents. User's
  groups not included in the `user-groups` are excluded for easier
  validation."
  [user-groups qualifications user]
  (when (and (seq user-groups) user)
    (-> user
        (select-keys [:db/id :user/email :user/first-name :user/last-name])
        (assoc
          :groups (->> user-groups
                       (map (partial group-with-checked user))
                       (into {}))
          :qualifications (->> qualifications
                               (map (partial group-with-checked user))
                               (into {}))))))

(defn validate-edit-state
  "Validates each field of `edit-state`. Returns field - error l10n key
  map, or nil if valid."
  [edit-state & [only-keys]]
  (let [groups     (->> (:groups edit-state)
                        (map (fn [[k v]]
                               (when (:checked? v)
                                 k)))
                        (remove nil?))
        edit-state (assoc edit-state :user/groups groups)]
    (not-empty (reduce (fn [acc k]
                         (p/assoc-if acc k (validate/validate-field k edit-state user-details-model)))
                       {}
                       (or only-keys (keys user-details-model))))))

(def fields-with-edit-state #{:groups :qualifications})

(defn update-edit-state
  "DRY function that returs the updated `db`"
  [db user-id field value extra]
  (if (contains? fields-with-edit-state field)
    (assoc-in db [::edit-states user-id field value :checked?] extra)
    (assoc-in db [::edit-states user-id field] value)))

;; Called by ::edit-state sub when needed.
(rf/reg-event-db ::init-edit-state
  (fn [db [_ user-id]]
    (let [user-groups    (::user-groups db)
          qualifications (::qualifications db)
          user           (get-in db [::users user-id])
          edit-state     (create-edit-state user-groups qualifications user)]
      (cond-> db
        edit-state (assoc-in [::edit-states user-id] edit-state)))))

(rf/reg-sub ::edit-state
  (fn [{session-user :session/user :as db} [_ user-or-id]]
    (let [user-id (get user-or-id :db/id user-or-id)]
      (if-let [state (get-in db [::edit-states user-id])]
        (assoc state :session-user? (= user-id (:db/id session-user)))
        (>evt [::init-edit-state user-id])))))

(rf/reg-sub ::all-groups
  (fn [[_ user] _]
    [(rf/subscribe [::edit-state user])
     (rf/subscribe [::ff/disabled? :feature-flag.id/domain-level-meetings])])
  (fn [[edit-state domain-level-meetings-disabled?] _]
    (->> edit-state
         :groups
         vals
         (remove #(and domain-level-meetings-disabled?
                       (group-model/has-role? :group.role/meeting-coordinator %))))))


(rf/reg-sub ::groups
  (fn [[_ user] _]
    (rf/subscribe [::all-groups user]))
  (fn [groups _]
    (->> groups
         (filter acommon/user-selectable-group?)
         (sort acommon/group-sort))))

(rf/reg-sub ::external-role-groups
  (fn [[_ user] _]
    (rf/subscribe [::groups user]))
  (fn [groups _]
    (filter external-model/role-group? groups)))

(rf/reg-sub ::external-department-groups
  (fn [[_ user] _]
    (rf/subscribe [::groups user]))
  (fn [groups _]
    (filter external-model/department-group? groups)))

;; A user holds one role of an external module at a time.
(rf/reg-sub ::external-role
  (fn [[_ user] _]
    (rf/subscribe [::external-role-groups user]))
  (fn [roles _]
    (p/find-first roles :checked?)))

(rf/reg-event-fx ::set-external-role
  ;; Choosing a role leaves the current one in the same edit.
  (fn [_ [_ user-id current-role chosen]]
    (when-let [changes (not-empty (cond-> []
                                    current-role (conj [(:db/id current-role) false])
                                    chosen       (conj [(:db/id chosen) true])))]
      {:dispatch [::update-multiple-groups-and-send user-id changes]})))

(defn- has-role-match? [{:group/keys [roles]} role-set]
  (-> roles
      set
      (set/intersection role-set)
      seq
      boolean))

(defn- categorize-group-by-roles
  "Categorizes a group as either :super, :domain or :authority based on its roles."
  [group]
  (cond
    (has-role-match? group group-model/superdomain-group-child-roles) :super
    (has-role-match? group group-model/domain-group-child-roles) :domain
    (has-role-match? group group-model/authority-group-child-roles) :authority))

(rf/reg-sub ::superdomain-grouped-groups
  (fn [[_ user] _]
    (rf/subscribe [::groups user]))
  (fn [groups _]
    (->> groups
         (group-by categorize-group-by-roles)
         (p/remove-vals nil?))))

(rf/reg-sub ::permission-groups
  (fn [[_ user] _]
    (rf/subscribe [::all-groups user]))
  (fn [groups _]
    (->> (filter acommon/write-auth-group? groups)
         (sort acommon/group-sort))))


(rf/reg-sub ::viewer-group
  (fn [[_ user] _]
    (rf/subscribe [::all-groups user]))
  (fn [groups]
    (p/find-first groups acommon/viewer-group?)))


(defn remove-qualifications
  "Marks all qualifications for given user ID as not checked"
  [db user-id]
  (let [edit-states             (get-in db [::edit-states user-id])
        qualifications          (:qualifications edit-states)
        mark-not-checked        (fn [m k v] (assoc m k (merge v {:checked? false})))
        disabled-qualifications (reduce-kv mark-not-checked {} qualifications)]
    (assoc-in db [::edit-states user-id :qualifications] disabled-qualifications)))

(defn selected-inspector-group? [inspectors-group-id group]
  (and (= (:db/id group) inspectors-group-id) (:checked? group)))

(defn remove-qualifications-if-not-inspector
  "Tests if the user is not in the Inspectors group.
  If not, removes the user from all Qualifications groups"
  [db user-id]
  (let [edit-state          (get-in db [::edit-states user-id])
        groups              (:groups edit-state)
        inspectors-group-id (::inspectors-group-id db)
        inspector?          (some (partial selected-inspector-group? inspectors-group-id) (vals groups))]
    (if-not inspector?
      (remove-qualifications db user-id)
      db)))


(defn set-groups-checked
  [db user-id group-filter checked?]
  (let [mark-checked (fn [m k v]
                       (assoc m k (if (group-filter v) (assoc v :checked? checked?) v)))
        check-groups (fn [groups] (reduce-kv mark-checked {} groups))]
    (update-in db [::edit-states user-id :groups] check-groups)))


(defn auto-check-groups [db user-id group-id checked?]
  (let [checked-group  (get-in db [::edit-states user-id :groups group-id])
        admin-group-id (get db ::gas/group-id)
        admin-group    (some->> admin-group-id (entities/find-entity (entities/db->context db)))
        superdomain?   (group-model/superdomain? admin-group)]
    (if checked?
      (cond
        ;; Non-superdomain: checking viewer unchecks all non-viewer groups
        (and (not superdomain?) (acommon/viewer-group? checked-group))
        (set-groups-checked db user-id #(-> % acommon/viewer-group? not) false)

        ;; Non-superdomain: checking a write group unchecks viewer
        (and (not superdomain?) (acommon/write-group? checked-group))
        (-> db
            (set-groups-checked user-id acommon/write-auth-group? true)
            (set-groups-checked user-id acommon/viewer-group? false))

        ;; Superdomain: checking a write group only auto-checks write-auth
        (and superdomain? (acommon/write-group? checked-group))
        (set-groups-checked db user-id acommon/write-auth-group? true)

        :else db)
      (if (acommon/org-admin-group? checked-group)
        (-> db
            (set-groups-checked user-id acommon/org-admin-group? false)
            (set-groups-checked user-id acommon/escrow-admin-group? false))
        db))))

(rf/reg-event-fx ::update-edit-state-and-send
  (fn [{db :db} [_ user-id field value extra]]
    (let [db             (-> db
                             (update-edit-state user-id field value extra)
                             (auto-check-groups user-id value extra)
                             (remove-qualifications-if-not-inspector user-id))
          edit-state     (get-in db [::edit-states user-id])
          ;; Group and qualification changes are always sent
          previous-value (when-not (and (= field :groups) (= field :qualifications))
                           (get-in db [::users user-id field]))]
      (p/assoc-if {:db db}
                  :dispatch (when (and (nil? (validate-edit-state edit-state
                                                                  (when (= field :groups)
                                                                    [:user/groups])))
                                       (not= value previous-value))
                              [::send-user-edits field edit-state])))))

(rf/reg-event-fx ::update-multiple-groups-and-send
  (fn [{db :db} [_ user-id group-changes]]
    (let [db         (-> (reduce (fn [db [gid checked?]]
                                   (-> db
                                       (update-edit-state user-id :groups gid checked?)
                                       (auto-check-groups user-id gid checked?)))
                                 db
                                 group-changes)
                         (remove-qualifications-if-not-inspector user-id))
          edit-state (get-in db [::edit-states user-id])]
      (p/assoc-if {:db db}
                  :dispatch (when (nil? (validate-edit-state edit-state [:user/groups]))
                              [::send-user-edits :groups edit-state])))))

(rf/reg-event-db ::update-edit-state
  (fn [db [_ user-id field value extra]]
    (update-edit-state db user-id field value extra)))

(rc/reg-chain* ::save-user-failure
  interceptors
  (fn [{:keys [tr]} [_ status body]]
    (js/console.error "::save-user" status body)
    {:dispatch [:notifications/add
                :inline
                :error
                (tr [:error/user-save-failure])
                (or (some-> body :error vector tr)
                    "")]}))

(rf/reg-event-fx ::toast-success
  interceptors
  (fn [{tr :tr}]
    {:dispatch [:notifications/add
                :toast
                :success
                (tr [:user-management/save-success])
                (tr [:user-management/can-continue])]}))

;; Called by `::update-edit-state-and-send` if the state passes validation.
(rc/reg-chain* ::send-user-edits
  interceptors
  (fn [_ [_ field {:keys [groups qualifications] :as edit-state}]]
    (let [cmd-fn     (fn [cmd data]
                       {:comm/command {:command                  cmd
                                       :data                     (assoc data :user-id (:db/id edit-state))
                                       :on-bad-request           [::save-user-failure]
                                       :on-internal-server-error [::save-user-failure]}})
          all-groups (concat groups qualifications)]
      (if (contains? fields-with-edit-state field)
        (cmd-fn :group/edit-user-groups
                {:add    (->> all-groups vals (filter :checked?) (map :db/id))
                 :remove (->> all-groups vals (remove :checked?) (map :db/id))})
        (cmd-fn :group/edit-user-names
                (case field
                  :user/first-name {:first-name (:user/first-name edit-state)}
                  :user/last-name {:last-name (:user/last-name edit-state)})))))

  (fn [{db :db} [_ field _ _ resp]]
    (let [{user-id :db/id
           :as     user}  (some-> resp vals first)]
      {:db         (if (= field :groups)
                     (let [available-groups (::user-groups db)
                           user-groups      (->> (:user/groups user)
                                                 (map #(p/find-first available-groups {:db/id %}))
                                                 (remove nil?)
                                                 (map #(select-keys % [:db/id :group/name]))
                                                 (sort-by :group/name))]
                       (assoc-in db [::users user-id :user/groups] user-groups))
                     (assoc-in db [::users user-id field] (get user field)))
       :dispatch-n [[::toast-success]
                    (when (= (get-in db [:session/user :db/id]) user-id)
                      [:core.front.session/restore-session])]})))

;; --  Other subscriptions -----------------------------------------------------------

(rf/reg-sub ::user-groups
  (fn [db]
    (some-> db ::user-groups not-empty)))


(rf/reg-sub ::users
  (fn [db]
    (->> db
         ::users
         vals
         (remove user-model/service-account?)
         not-empty)))

(rf/reg-sub ::review-assignment-group-names
  ;; From the fetched user: the table's has been through JS, which turns the
  ;; group roles into strings.
  (fn [db [_ user-id]]
    (user-management-model/review-assignment-group-names (get-in db [::users user-id]))))

(rf/reg-sub ::service-account-users
  (fn [db]
    (->> db
         ::users
         vals
         (filter user-model/service-account?)
         (sort-by :user/username))))

(rf/reg-sub ::group
  (fn [db]
    (<sub [::entities/entity (::group-id db)])))

;; --  Remove from inspectors confirmation dialog ------------------------------------

(rf/reg-event-db ::remove-from-inspectors
  (fn [db [_ evt user-name]]
    (-> db
        (assoc-in [::remove-from-inspectors-confirm :open?] true)
        (assoc-in [::remove-from-inspectors-confirm :event] evt)
        (assoc-in [::remove-from-inspectors-confirm :user-name] user-name))))

(defn- close-remove-from-inspectors-confirm
  [db]
  (-> db
      (assoc-in [::remove-from-inspectors-confirm :open?] false)
      (p/dissoc-in [::remove-from-inspectors-confirm :event])
      (p/dissoc-in [::remove-from-inspectors-confirm :user-name])))

(rf/reg-event-fx ::confirm-remove-from-inspectors
  (fn [{:keys [db]} [_]]
    (let [event (get-in db [::remove-from-inspectors-confirm :event])]
      {:db       (close-remove-from-inspectors-confirm db)
       :dispatch event})))

(rf/reg-event-db ::cancel-remove-from-inspectors
  (fn [db]
    (swap! *viewer-only-force inc)
    (close-remove-from-inspectors-confirm db)))

(rf/reg-sub ::remove-from-inspectors-confirm-open
  (fn [db]
    (-> db ::remove-from-inspectors-confirm :open?)))

(rf/reg-sub ::remove-from-inspectors-confirm-user-name
  (fn [db]
    (-> db ::remove-from-inspectors-confirm :user-name)))

;; -----------------------
;; External email handling.

(defn parse-emails
  "Parses properly formatted emails addresses from `txt`. Addresses can
  be separated by whitespace, comma or semicolon. Returns a map with
  either `:emails` (list) or `:error` (string). Nil if `txt` is blank."
  [tr txt]
  (when-not (str/blank? txt)
    (let [emails (str/split txt #"[\s,;]+")]
      (if-let [bad (p/find-first emails validate/check-email)]
        {:error (tr [:user-management/email-error] [bad])}
        {:emails emails}))))

(rf/reg-sub ::email-recipients-group
  :<- [::user-groups]
  (fn [user-groups]
    (p/find-first user-groups #(contains? (set (:group/roles %))
                                          :group.role/email-recipient))))

(rf/reg-sub ::emails?
  (fn [db [_ k]]
    (boolean (get-in db [::emails k]))))

(rf/reg-sub ::fetching-users? :-> ::fetching-users?)

(rf/reg-event-db ::emails
  (fn [db [_ k v]]
    (assoc-in db [::emails k] v)))

(rc/reg-chain ::emails-save
  (fn [_ [_ group-id op emails]]
    {:comm/command {:command :group/edit-emails
                    :data    (assoc {:group-id group-id}
                                    op
                                    emails)}})
  (fn [_]
    {:dispatch-n [[::emails :open? false]
                  [::fetch-groups]
                  [::toast-success]]}))

;; -- Other events ------------------------------------------------------------------

(rf/reg-event-db ::init
  interceptors
  (fn [db [_ group-id]]
    (-> db
        (assoc ::group-id group-id)
        (assoc ::remove-from-inspectors-confirm {:open? false})
        (dissoc ::user-groups ::inspectors-group-id ::qualifications ::users ::edit-states ::emails))))

(rc/reg-chain* ::fetch-groups
  interceptors
  (fn [{:keys [db]}]
    {:comm/command {:command :group/get-user-groups
                    :method  :get
                    :data    {:parent-group-id (::group-id db)}}})
  (fn [{:keys [db]} [_ _ user-groups]]
    (let [inspectors-group? #(-> % :group/roles set :group.role/inspector)
          inspectors-group  (first (filter inspectors-group? user-groups))]
      {:db (assoc db
                  ::user-groups user-groups
                  ::inspectors-group-id (:db/id inspectors-group)
                  ;; Qualifications are child groups of the Inspectors group
                  ::qualifications (:qualifications inspectors-group))})))

(rf/reg-sub ::inspectors-group-id
  (fn [db]
    (get db ::inspectors-group-id)))

(rf/reg-sub ::groups-loaded?
  ;; The forms are built from the user-groups.
  (fn [db]
    (contains? db ::user-groups)))

(rf/reg-event-fx ::fetch-users
  interceptors
  (fn [{db :db} _]
    {:db           (assoc db ::fetching-users? true)
     :comm/command {:command        :group/get-users
                    :method         :get
                    :data           {:parent-group-id (::group-id db)}
                    :on-success     [::fetch-users.success]
                    :on-bad-request [::fetch-users-failure]}}))

(rf/reg-event-fx ::fetch-users.success
  (fn [{:keys [db]} [_event _status resp]]
    {:db (assoc db ::users (p/index-by :db/id resp) ::fetching-users? false)}))

(rc/reg-chain ::fetch-users-failure
  (fn [{:keys [db]} [_ status body]]
    (js/console.error "::fetch-users-failure" status body)
    {:db (assoc db ::fetching-users? false)}))

(rf/reg-event-db ::add-new-user-to-list
  (fn [db [_ new-user-data]]
    (assoc-in db [::users (:db/id new-user-data)] new-user-data)))


(rf/reg-event-db ::remove-user
  interceptors
  (fn [db [_ user-id]]
    (-> db
        (p/dissoc-in [::users user-id])
        (p/dissoc-in [::edit-states user-id]))))

(rf/reg-event-fx ::set-user-mfa-status
  (fn [_ [_ data]]
    {:comm/command {:command    :group/set-user-mfa-status
                    :method     :post
                    :data       data
                    :on-success [::set-user-mfa-status.success data]}}))

(rf/reg-event-db ::set-user-mfa-status.success
  (fn [db [_ {:mfa-config/keys [id] :keys [user-id enabled?]}]]
    (update-in db [::users user-id] (fn [user]
                                      (if enabled?
                                        (assoc-in user [:user/mfa-config :mfa-config/id] id)
                                        (dissoc user :user/mfa-config))))))

(defn toolbar [tr expand-row-handler expanded?]
  [:div.flex.flex-1.justify-end
   (if expanded?
     (cc/button
       {:kind             :ghost
        :size             :small
        :icon             i/remove
        :icon-description (tr [:user-management/close])
        :on-click         expand-row-handler}
       (tr [:user-management/close]))
     (cc/button
       {:kind             :ghost
        :size             :small
        :icon             i/pen
        :icon-description (tr [:user-management/edit])
        :on-click         expand-row-handler}
       (tr [:user-management/edit])))])


(defn- group-role-found? [user-groups role]
  (->> (mapcat :group/roles user-groups)
       (remove nil?)
       set
       role))


(defn remove-inspectors-group-modal [tr]
  (let [cancel-fn #(>evt [::cancel-remove-from-inspectors])
        user-name (<sub [::remove-from-inspectors-confirm-user-name])
        open?     (boolean (<sub [::remove-from-inspectors-confirm-open]))]
    (cc/modal {:open                  open?
               :primary-button-text   (tr [:user-management/remove])
               :secondary-button-text (tr [:user-management/cancel])
               :modal-heading         (tr [:user-management/inspectors-remove-heading])
               :on-request-close      cancel-fn
               :on-request-submit     #(>evt [::confirm-remove-from-inspectors])
               :on-secondary-submit   cancel-fn
               :danger                true}
              [:p.bx--modal-content__text
               (tr [:user-management/inspectors-remove-confirmation] [user-name])])))

(defn- remove-admin-disabled?
  "Admin cannot demote herself."
  [checked? session-user? group-id admin-group-ids]
  (boolean (and checked?
                session-user?
                (contains? admin-group-ids group-id))))

(defn- remove-from-group-disabled?
  "A user group used in group config should not be empty."
  [checked? {:keys [used-in-group-config?]} user-count]
  (boolean (and checked?
                used-in-group-config?
                (< user-count 2))))

(defn- remove-last-group-disabled?
  "User must have at least one group."
  [checked? selected-groups-count group groups]
  (and checked?
       (or (< selected-groups-count 2)
           (and (acommon/org-admin-group? group)
                (empty? (filter #(and (:checked? %)
                                      (not (= group %))
                                      (not (acommon/escrow-admin-group? %)))
                                groups))))))

(defn escrow-admin-disabled? [checked? group groups]
  (let [escrow-admin? (acommon/escrow-admin-group? group)
        org-admin?    (->> groups
                           (some #(and (:checked? %)
                                       (acommon/org-admin-group? %))))]
    (and (not checked?)
         escrow-admin?
         (not org-admin?))))

(defn group-checkboxes [inspectors-group-id session-user? user-id user-name admin-gids all-groups
                        groups must-have-one-group?]
  (let [selected-groups-count (->> all-groups (filter :checked?) count)]
    (for-frag [{gid      :db/id
                gname    :group/name
                checked? :checked?
                :as      group} groups
               :let [inspectors-group? (= gid inspectors-group-id)]]
      (let [user-count (<sub [::user-count gid])]
        (cc/checkbox {:id         (str "toggle-" user-id "-" gid)
                      :data-cy    "user-groups-checkbox"
                      :key        (str "toggle-" user-id "-" gid)
                      :label-text gname
                      :title      gname
                      :disabled   (or (remove-admin-disabled? checked? session-user? gid admin-gids)
                                      (remove-from-group-disabled? checked? group user-count)
                                      (and must-have-one-group?
                                           (remove-last-group-disabled? checked?
                                                                        selected-groups-count
                                                                        group
                                                                        groups))
                                      (escrow-admin-disabled? checked? group groups))
                      :checked    checked?
                      :on-change  #(if (and inspectors-group? checked?)
                                     ;; Unchecking inspectors group: confirm removal
                                     (>evt [::remove-from-inspectors
                                            [::update-multiple-groups-and-send user-id [[gid %]]]
                                            user-name])
                                     (>evt [::update-edit-state-and-send user-id :groups gid %]))})))))


(defn viewer-only-selection [tr viewer-group viewer-only? is-inspector? user-id user-name]
  ^{:key (str "viewer-only-selection" @*viewer-only-force)}
  [:div
   [:div.mb-2.text-xs (tr [:user-management/viewer-only])]
   [yes-no/yes-no-radio-buttons tr (str "viewer-only-" user-id)
    {:value-selected viewer-only?
     :on-change      (fn [checked]
                       (if (and is-inspector? checked)
                         (>evt [::remove-from-inspectors
                                [::update-edit-state-and-send user-id :groups (:db/id viewer-group) checked]
                                user-name])
                         (>evt [::update-edit-state-and-send user-id :groups (:db/id viewer-group) checked])))}]])

(defn viewer-permission-checkbox [tr viewer-group viewer-permission? last-checked? is-inspector? inspectors-group-id user-id user-name]
  [:div
   (cc/checkbox {:id         (str "viewer-permission-" user-id)
                 :data-cy    "viewer-permission-checkbox"
                 :key        (str "viewer-permission-" user-id)
                 :label-text (tr [:user-management/view-permission])
                 :checked    viewer-permission?
                 :disabled   (and viewer-permission? last-checked?)
                 :on-change  (fn [checked]
                               (if (and is-inspector? (not checked))
                                 (>evt [::remove-from-inspectors
                                        [::update-multiple-groups-and-send user-id
                                         [[(:db/id viewer-group) checked] [inspectors-group-id false]]]
                                        user-name])
                                 (>evt [::update-edit-state-and-send user-id :groups (:db/id viewer-group) checked])))})])

(defn- render-groups-section
  "Renders a section of groups with optional heading and layout."
  [groups checkboxes heading tr]
  [:div.mt-2
   (when heading
     [:div.bx--label.font-semibold (tr [heading])])
   (if (> (count groups) 5)
     [:div.flex.flex-wrap.gap-x-4
      (for-frag [subgroups (partition-all 4 groups)]
        [:div.flex.flex-col
         (checkboxes subgroups false)])]
     (checkboxes groups false))])

(defn superdomain-grouped-selector
  "For superdomain groups, split groups by domain/authority roles"
  [{:keys [tr user]} checkboxes]
  (let [{:keys [super domain authority]} (<sub [::superdomain-grouped-groups user])]
    [:div.mt-2
     [:div.bx--label (tr [:user-management/groups])]
     (when super
       [render-groups-section super checkboxes :user-management/superdomain-user-groups tr])
     (when domain
       [render-groups-section domain checkboxes :user-management/domain-user-groups tr])
     (when authority
       [render-groups-section authority checkboxes :user-management/authority-user-groups tr])]))

(defn- external-module-groups-selector
  "The groups of a product another system implements (PWAM): its roles,
  of which a user holds one at a time — a `<select>`, where choosing a role
  leaves the current one in the same edit — and its departments, any number
  of them."
  [tr {user-id :db/id :as user} checkboxes]
  (let [roles        (<sub [::external-role-groups user])
        departments  (<sub [::external-department-groups user])
        current-role (<sub [::external-role user])]
    [:<>
     [:div.mt-2
      [:div.bx--label (tr [:user-management/external-role])]
      [:p.text-xs.text-gray-600.mb-1 (tr [:user-management/external-role-help])]
      [:div {:class "md:w-1/2"}
       [ui-select/select {:id         (str "external-role-" user-id)
                          :data-cy    "external-role-select"
                          :tr         tr
                          :label-text (tr [:user-management/external-role])
                          :hide-label true
                          :values     roles
                          :val->str   :group/name
                          :empty-text (tr [:admin/tenant-external-role-none])
                          :value      current-role
                          :on-change  #(>evt [::set-external-role user-id current-role %])}]]]
     (when (seq departments)
       [:div.mt-2
        [:div.bx--label (tr [:user-management/external-departments])]
        (checkboxes departments false)])]))

(defn user-groups-selector [{:keys [tr user-groups user] :as opts}]
  (let [{user-id       :db/id
         session-user? :session-user?
         :as           edit-state}    (<sub [::edit-state user])
        permissions                   (<sub [::permission-groups user])
        groups                        (<sub [::groups user])
        viewer-group                  (<sub [::viewer-group user])
        viewer-only?                  (:checked? viewer-group)
        user-name                     (user-model/full-name edit-state)
        invalid-txt                   (some-> (validate-edit-state edit-state)
                                              :user/groups
                                              vector
                                              tr)
        invalid?                      (boolean invalid-txt)
        admin-gids                    (du/entity-id-set (filter :admin-group? user-groups))
        inspectors-group-id           (<sub [::inspectors-group-id])
        is-inspector?                 (some (fn [{checked? :checked? id :db/id}] (and (= id inspectors-group-id)
                                                                                      checked?))
                                            groups)
        checkboxes                    (partial group-checkboxes inspectors-group-id
                                               session-user? user-id user-name admin-gids groups)
        superdomain?                  (<sub [::gas/superdomain-group?])
        external-module?              (group-model/external-module? (<sub [::gas/group]))
        total-checked                 (when superdomain?
                                        (+ (->> groups (filter :checked?) count)
                                           (->> permissions (filter :checked?) count)
                                           (if viewer-only? 1 0)))
        last-checked?                 (and superdomain? (= total-checked 1))]
    [:<>
     [:div
      [:div.bx--form-item.pb-2
       {:id               (str "user-groups-checkboxes#" user-id)
        :data-invalid     invalid?
        :aria-invalid     invalid?
        :aria-describedby (when invalid?
                            (str "user-groups-checkboxes#" user-id "-error-msg"))}
       [:div
        (cond
          ;; An external module has no permissions or viewers, only roles and departments.
          external-module?
          [external-module-groups-selector tr user checkboxes]

          superdomain?
          ;; Superdomain: viewer checkbox + always show permissions & groups
          [:<>
           [:div.mt-2
            [:div.bx--label (tr [:user-management/permissions])]
            (when-not session-user? ;; Prevent admin from demoting themselves.
              [viewer-permission-checkbox tr viewer-group viewer-only? last-checked?
               is-inspector? inspectors-group-id user-id user-name])
            (when (seq permissions)
              (checkboxes permissions false))]
           (when (seq groups)
             [superdomain-grouped-selector opts checkboxes])]

          :else
          ;; Non-superdomain: viewer-only radio + hide permissions/groups when viewer-only
          [:<>
           (when (and viewer-group (not session-user?)) ;; Prevent admin from demoting themselves.
             [viewer-only-selection tr viewer-group viewer-only? is-inspector? user-id user-name])
           (when-not viewer-only?
             [:<>
              [:div.mt-2
               [:div.bx--label (tr [:user-management/permissions])]
               (when (seq permissions)
                 (checkboxes permissions false))]
              (when (seq groups)
                [:div.mt-2
                 [:div.bx--label (tr [:user-management/groups])]
                 (if (> (count groups) 5)
                   [:div.flex.flex-wrap.gap-x-4
                    (for-frag [subgroups (partition-all 4 groups)]
                      [:div.flex.flex-col
                       (checkboxes subgroups true)])]
                   (checkboxes groups true))])])])
        [:div.bx--form-requirement invalid-txt]]]]]))

(defn qualifications-list [qualifications user-id tr]
  (let [lang (<sub [:language])]
    [:fieldset.bx--fieldset {:class "md:w-1/2"}
     [:legend.bx--label (tr [:user-management/qualifications])]
     (for-frag [{gid                 :db/id
                 gname               :group/name
                 qualification-names :qualification-names
                 checked?            :checked?} (->> qualifications vals (sort-by :group/name))
                :let [id    (str "toggle-" user-id "-" gname)
                      title (or (i18n-model/get-text qualification-names lang)
                                (tr [(keyword (str "qualification.type/" gname))]))]]
       (cc/checkbox {:id              id
                     :key             id
                     :label-text      title
                     :title           title
                     :default-checked checked?
                     :on-change       #(>evt [::update-edit-state-and-send user-id :qualifications gid %])}))]))

(defn mfa-button [{:keys [tr user]}]
  (when-let [config (first (<sub [::group.admin.mfa/mfa-configs]))]
    (let [user-mfa                   (:user/mfa-config user)
          current-group-mfa-enabled? (= (:mfa-config/id user-mfa) (:mfa-config/id config))]
      (if (or current-group-mfa-enabled?
              (not user-mfa))
        (cc/button
          {:kind       :tertiary
           :size       :small
           :class-name "mr-2"
           :icon       (if current-group-mfa-enabled? i/lock-open i/lock)
           :on-click   #(>evt [::set-user-mfa-status (merge (select-keys config [:mfa-config/id])
                                                            {:user-id  (:db/id user)
                                                             :enabled? (nil? user-mfa)})])}
          (tr [(if current-group-mfa-enabled?
                 :user-management/disable-mfa
                 :user-management/enable-mfa)]
              [(:mfa-config/display-name config)]))
        [:span.mr-3 (tr [:user-management/other-mfa-enabled]
                        [(:mfa-config/display-name user-mfa)])]))))

(defn user-details [{:keys [tr user] :as options}]
  (let [{user-id        :db/id
         groups         :groups
         qualifications :qualifications
         :as            edit-state}              (<sub [::edit-state user])
        inspectors-group-id                      (<sub [::inspectors-group-id])
        inspector-group?                         (fn [g] (and
                                                           (= (:db/id g) inspectors-group-id)
                                                           (:checked? g)))
        inspector?                               (some inspector-group? (vals groups))
        {first-name-invalid-txt :user/first-name
         last-name-invalid-txt  :user/last-name} (p/map-vals (comp tr vector)
                                                             (validate-edit-state edit-state))
        value-fn                                 #(-> % .-target .-value str/trim)]
    (if edit-state
      [:<>
       [:div.flex
        [:div {:class "mb-6 pr-4 w-1/3"}
         [ui/text-input {:id            (str "input-user/first-name#" (:db/id user))
                         :type          :text
                         :label-text    (tr [:user-management/first-name])
                         :default-value (:user/first-name edit-state)
                         :invalid       (boolean first-name-invalid-txt)
                         :invalid-text  first-name-invalid-txt
                         :on-change     #(>evt [::update-edit-state user-id
                                                :user/first-name (value-fn %)])
                         :on-blur       #(>evt [::update-edit-state-and-send user-id
                                                :user/first-name (value-fn %)])}]]
        [:div {:class "mb-6 pr-4 w-1/3 mr-auto"}
         [ui/text-input {:id            (str "input-user/last-name#" (:db/id user))
                         :type          :text
                         :label-text    (tr [:user-management/last-name])
                         :default-value (:user/last-name edit-state)
                         :invalid       (boolean last-name-invalid-txt)
                         :invalid-text  last-name-invalid-txt
                         :on-change     #(>evt [::update-edit-state user-id
                                                :user/last-name (value-fn %)])
                         :on-blur       #(>evt [::update-edit-state-and-send user-id
                                                :user/last-name (value-fn %)])}]]
        [:div {:class "mb-6 pr-4 w-1/3"}
         [ui/text-input {:id            (str "input-user/email#" (:db/id user))
                         :type          :email
                         :label-text    (tr [:user-management/email])
                         :disabled      true
                         :default-value (:user/email edit-state)}]]]
       [:div.flex
        (cc/form-group
          {:class-name  "md:w-1/2"
           :legend-text ""}
          [user-groups-selector options])
        ;; Show Inspector qualifications when Inspectors group is selected
        (when (and inspector? (seq qualifications))
          [qualifications-list qualifications user-id tr])]
       (when-let [assignment-group-names (<sub [::review-assignment-group-names (:db/id user)])]
         [:div.mb-6
          [:div.text-sm.text-gray-600 (tr [:review-assignment-group/belongs-to])]
          [:div (str/join ", " assignment-group-names)]])
       [:div.flex.justify-end.items-center
        [mfa-button (util/mmap user tr)]
        [remove-user/remove-button options]]]
      (cc/skeleton-text {}))))

(defn approver-details
  [options]
  [:div.bx--row
   [:div.bx--col-xs-6.bx--offset-xs-1
    [user-groups-selector options]]
   [:div.bx--col-xs-4.bx--offset-xs-1.flex.flex-col.items-end
    [remove-user/remove-button options]]])

(defn restore-keys-namespace [user]
  (-> user
      (update :groups
              (fn [groups]
                (map #(set/rename-keys % {:id   :db/id
                                          :name :group/name})
                     groups)))
      (update :mfa-config set/rename-keys {:id           :mfa-config/id
                                           :display-name :mfa-config/display-name})
      (set/rename-keys {:id         :db/id
                        :username   :user/username
                        :last-name  :user/last-name
                        :first-name :user/first-name
                        :email      :user/email
                        :groups     :user/groups
                        :mfa-config :user/mfa-config})))


(defn render-table [tr group-id current-user user-groups props]
  (let [{:keys [rows headers getHeaderProps getRowProps expandRow]} (js->clj props :keywordize-keys true)
        admin?                                                      (<sub [:session/admin?])]
    (r/as-element
      (cc/table-container
        (cc/table
          {:id "table-users-with-groups"}
          (cc/table-head
            {}
            (cc/table-row
              {}
              (cc/table-expand-header)
              (for-frag [header headers
                         :let [header-key   (keyword (:key header))
                               header-props (js->clj (getHeaderProps #js {:header   (clj->js header)
                                                                          :children (:header header)})
                                                     :keywordize-keys true)]]
                (case header-key
                  :actions (cc/table-header (-> header-props (dissoc :children) (assoc :isSortable false)))
                  (cc/table-header header-props)))))
          (cc/table-body
            {}
            (for-frag [row rows
                       :let [row-id             (:id row)
                             row-props          (js->clj (getRowProps #js {:row                   (clj->js row)
                                                                           :expandIconDescription (tr [:carbon.table.row.expand])})
                                                         :keywordize-keys true)
                             expand-row-handler #(expandRow row-id)
                             expanded?          (:isExpanded row)]]
              [:<>
               (cc/table-expand-row
                 (assoc row-props :key row-id)
                 (for-frag [cell (:cells row)
                            :let [cell-id (:id cell)
                                  value   (-> cell :value)
                                  header  (-> cell :info :header keyword)]]
                   (case header
                     :oidc-enabled (cc/table-cell
                                     {:key cell-id}
                                     (if value
                                       [:span {:title (tr [:user-management/sso-enabled])}
                                        (r/create-element SingleSignOn
                                                          #js{:title  (tr [:user-management/sso-enabled])
                                                              :width  32
                                                              :height 32
                                                              :fill   "#4F0891"})]
                                       ""))
                     :actions (cc/table-cell
                                {:key cell-id}
                                [toolbar tr expand-row-handler expanded?])
                     (cc/table-cell {:key cell-id} value))))
               (when expanded?
                 (let [user             (->> row
                                             :cells
                                             (some #(when (= :actions (-> % :info :header keyword)) %))
                                             :value
                                             restore-keys-namespace)
                       remove-disabled? (and (= (:db/id current-user) (:db/id user))
                                             (not admin?))
                       component        (if (group-role-found? user-groups :group.role/approver)
                                          approver-details
                                          user-details)]
                   (cc/table-expanded-row
                     {:key      (str "table-expanded-row-user-details#" (:db/id user))
                      :col-span (inc (count headers))}
                     [component {:tr               tr
                                 :group-id         group-id
                                 :user             user
                                 :user-groups      user-groups
                                 :remove-disabled? remove-disabled?}])))])))))))

(defn- prepare-user [user-groups user]
  (-> user
      (update :db/id str)
      (assoc :user/full-name (user-model/full-name user))
      (update :user/groups (fn [groups]
                             (let [gids (-> user-groups du/entity-id-set)]
                               (->> groups
                                    (filter (comp gids :db/id))
                                    (map :group/name)
                                    sort
                                    (str/join ", ")))))
      (assoc :oidc-enabled (-> user :user/oidc-config some?))
      (assoc :actions user)))

(rf/reg-sub ::prepared-users
  :<- [::users]
  :<- [::user-groups]
  (fn [[users user-groups] _]
    (some->> users
             (map (partial prepare-user user-groups))
             (sort-by :user/full-name))))

(rf/reg-sub ::user-count
  :<- [::users]
  (fn [users [_ group-id]]
    (->> users
         (filter #(-> % :user/groups du/entity-id-set (contains? group-id)))
         count)))

(defn user-management-form [tr group-id user user-groups]
  (let [groups-loaded? (<sub [::groups-loaded?])]
    [section/section
     :user-management-section
     (util/mmap {:heading-button (ui/button
                                   {:icon             i/user-plus
                                    :data-cy          "add-new-user"
                                    :size             :small
                                    :icon-description (tr [:user-management/add])
                                    :disabled         (not groups-loaded?)
                                    :on-click         (add-user/handle-open group-id user-groups [::fetch-users])}
                                   (tr [:user-management/add]))}
                tr)
     (tr [:user-management/title])
     (if (and groups-loaded? (not (<sub [::fetching-users?])))
       (if-let [users (<sub [::prepared-users])]
         (cc/data-table {:headers     [{:key :user/full-name :header (tr [:user-management/name])}
                                       {:key :user/email :header (tr [:user-management/email])}
                                       {:key :user/groups :header (tr [:user-management/groups])}
                                       {:key :oidc-enabled :header ""}
                                       {:key :actions :header ""}]
                         :is-sortable true
                         :rows        users
                         :render      (partial render-table tr group-id user user-groups)})
         [:div.text-xl.text-gray-400 (tr [:user-management/no-users-found])])
       (cc/data-table-skeleton))]))

;; ----------------------------
;; External email recipients
;; ----------------------------

(defn add-emails-modal [tr]
  (let [open?     (boolean (<sub [::emails? :open?]))
        cancel-fn #(>evt [::emails :open? false])
        group-id  (:db/id (<sub [::email-recipients-group]))]
    (r/with-let [txt* (r/atom "")]
      (let [{:keys [error emails]} (parse-emails tr @txt*)]
        [ui-modal/composed-input-modal
         {:open                   open?
          :selector-primary-focus "#email-recipients-textarea"
          :on-close               cancel-fn}
         (cc/modal-header {:close-modal cancel-fn
                           :title       (tr [:user-management/email-add])})

         [:div.w-full.p-8.flex.flex-col.items-center
          (if open?
            (cc/modal-body
              {:class-name "overflow-visible"}
              [ui/text-area {:id           "email-recipients-textarea"
                             :label-text   (tr [:user-management/email-add-label])
                             :rows         10
                             :invalid      error
                             :invalid-text error
                             :on-change    #(reset! txt* (.. % -target -value))}])
            (reset! txt* ""))]
         (cc/modal-footer
           {}
           (cc/button
             {:kind     :secondary
              :on-click cancel-fn}
             (tr [:cancel]))
           (cc/button
             {:kind     :primary
              :disabled (not emails)
              :on-click #(>evt [::emails-save group-id :add emails])}
             (tr [:save])))]))))

(defn email-management-form [tr]
  (let [email-group (<sub [::email-recipients-group])
        editing?    (<sub [::emails? :editing?])]
    (when email-group ;; if email recipient group exists, assume manual email management is applicable too
      [section/section
       :email-management-section
       (util/mmap {:heading-button (cc/button
                                     {:kind       (if editing? :secondary :primary)
                                      :class-name "align-middle"
                                      :size       :small
                                      :on-click   #(>evt [::emails :editing? (not editing?)])}
                                     (tr [(if editing? :admin/stop-editing :admin/edit)]))}
                  tr)
       (tr [:user-management/email-form-title])
       [:fieldset
        (when-not editing?
          [:div.mb-4 (tr [:user-management/email-info])])
        [:div.table.mb-8.ml-4
         (for-frag [email (sort (:notification/emails email-group))]
           [:div.table-row {:key email}
            [:div.table-cell.align-middle.pr-4
             {:class (futil/css-flags :py-2 editing?
                                      :py-1 (not editing?))} email]
            (when editing?
              [:div.table-cell.align-middle {:key (str "remove-" email)}
               [ui/modal-confirmation-button
                {:tr                tr
                 :id                "remove-email"
                 :trigger-icon-opts {:icon        i/remove
                                     :class-name  "cursor-pointer"
                                     :fill        "#cc1b00"
                                     :description (tr [:user-management/email-remove])}
                 :on-yes-click      #(>evt [::emails-save (:db/id email-group) :remove [email]])}
                [:div (tr [:user-management/email-remove-confirmation] [email])]]])])]
        (when editing?
          (cc/button
            {:kind             :primary
             :size             :small
             :icon             i/plus
             :icon-description (tr [:user-management/email-add])
             :on-click         #(>evt [::emails :open? true])}
            (tr [:user-management/email-add])))]])))

;; --------------------
;; Main view
;; --------------------


(defn view [match]
  (let [tr          (<sub [::gas/group-tr])
        user        (<sub [:session/user])
        user-groups (<sub [::user-groups])
        group-id    (-> match :parameters :path :group-id)
        group       (<sub [::group])]
    [acommon/admin-page-v2
     (util/mmap {:title (tr [:user-management/title])} match)
     [:div
      [user-management-form tr group-id user user-groups]
      [add-user/modal tr]
      [remove-user/modal tr]
      [remove-inspectors-group-modal tr]
      [email-management-form tr]
      [add-emails-modal tr]
      ;; The section's add form is built from the user-groups.
      (when (and (group-model/use-api-keys? group) (<sub [::groups-loaded?]))
        [service-accounts/section (util/mmap {:service-accounts (<sub [::service-account-users])
                                              :group-editor     user-groups-selector}
                                             tr group user-groups)])]]))

(rf/reg-sub ::back-href
  :<- [::user-groups]
  :<- [::gas/group]
  (fn [[user-groups group]]
    (let [approver-group? (->> user-groups
                               (some (partial group-model/has-role? :group.role/approver)))]
      (if approver-group?
        ;; Want to get back to domain approver groups if managing one of them.
        (href/href :view/group.admin.approver-groups-domain {:group-id (-> group :entity/parent :db/id)})
        (href/href :dashboard)))))

(rf/reg-sub ::view-data
  :<- [::back-href]
  (fn [back-href]
    {:back-href back-href}))

(def user-management-view
  {:name        :view/group.admin.user-management-view
   :view        view
   :view-sub    [::view-data]
   :public?     false
   :own-layout? true
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [{{:keys [group-id]} :path}]
                                (>evt [::init group-id])
                                (>evt [::fetch-groups])
                                (>evt [::fetch-users])
                                (>evt [::group.admin.mfa/fetch-mfa-configs group-id]))}]})
